import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// @ts-ignore: allow side-effect CSS import without module declarations
import "react-toastify/dist/ReactToastify.css";
import LoadingScreen from "../../../../shared/components/LoadingScreen";
import OrdersToolbar from "./OrdersToolbar";
import DeleteDialog from "./DeleteDialog";
import CommonTable, { HeadCell } from "../../../grocery/components/CommonTable";
import { useOrders } from "../../../../shared/hooks/useOrders";
import useImport from "./useImport";
import useExport from "./useExport";
import { Box, IconButton, TablePagination, Tooltip } from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteIcon from "@mui/icons-material/Delete";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";
import { useUser } from "src/shared/context/UserContext";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import { Button } from "src/business-types/grocery/components/Button";
import { splitDateTime } from "src/shared/components/dateTime";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";
import { statusColors } from "src/business-types/grocery/layouts/orders/OrderSidePanal";

export const formatNumber = (value: number) => {
  return value < 1000 ? value.toString().padStart(4, "0") : value.toString();
};

interface OrderTableRow {
  id: string | number;
  "S.No": number;
  Name: string;
  "Phone number": string;
  "Order ID"?: number | string;
  BillNo?: string;
  "Order status": React.ReactNode; // Changed from string to React.ReactNode
  "Date / Time": React.ReactNode; // Changed from string to React.ReactNode
  "Amount": string | number;
  "createdBy": string;
  Actions: React.ReactNode;
}

const OrderSearch: React.FC = () => {
  const navigation = useNavigate();
  const [, setBusinessType] = useState<string>("");
  const [isEmployee, setIsEmployee] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { userConfigration, user } = useUser();
  const { inputRef, handleImport } = useImport({ setLoading });
  const { handleExport } = useExport({ setLoading });
  const { sidebarThemeClass } = useUserTheme();
  const { formatDate, formatTime } = useDateTimeFormatter();

  const {
    orders,
    loadings,
    page,
    rowsPerPage,
    paginationData,
    searchTerm,
    selectedOption,
    selectedDate,
    selectedDate2,
    statusFilter,
    handleSearch,
    handleFilterChange,
    applyCustomDateFilter,
    setSelectedDate,
    setSelectedDate2,
    handleStatusFilterChange,
    handleDeleteOrder,
    handleChangePage,
    handleChangeRowsPerPage,
    getDateRangeForExport,
  } = useOrders({});

  useEffect(() => {
    document.title = pageTitle("Orders");
    setBusinessType(user?.businessType ?? "");
    setIsEmployee(!!sessionStorage.getItem("employee"));
  }, []);

  const handleViewOrder = (orderId: string) => {
    navigation(isEmployee ? `/v1/employee/order-details/${orderId}` : `/v1/order-details/${orderId}`);
  };

  const handleOpenDialog = (orderId: string) => {
    setSelectedOrderId(orderId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrderId(null);
  };

  const handleConfirmDelete = () => {
    if (selectedOrderId !== null) handleDeleteOrder(selectedOrderId);
    handleCloseDialog();
  };

  const toBoolean = (value: any) => value === true || value === "true";

  const billNoEnabled = toBoolean(userConfigration?.billNoEnabled);
  const orderIdEnabled = toBoolean(userConfigration?.orderIdEnabled);

  // ✅ Dynamic Head Cells
  const orderHeadCells: HeadCell<OrderTableRow>[] = [
    { id: "S.No" as const, label: "S.No" },
    { id: "Name" as const, label: "Name" },
    { id: "Phone number" as const, label: "Phone number" },
  ];

  if (orderIdEnabled) {
    orderHeadCells.push({ id: "Order ID" as const, label: "Order ID" });
  }

  if (billNoEnabled) {
    orderHeadCells.push({ id: "BillNo" as const, label: "Bill No." });
  }
  
  orderHeadCells.push({ id: "Date / Time" as const, label: "Date / Time" });
  orderHeadCells.push({ id: "Amount" as const, label: "Amount(QAR)", align: "center" });
  orderHeadCells.push({ id: "createdBy" as const, label: "Created By" });
  orderHeadCells.push({ id: "Order status" as const, label: "Order status" });
  orderHeadCells.push({ id: "Actions" as const, label: "Actions" });

  // ✅ Transform Orders Safely with Status Colors
  const transformedOrders: OrderTableRow[] = orders
    .filter((order) => order?.orders?.length && order.orders[0]?.status !== "deleted")
    .map((order, index) => {
      const baseOrder = order.orders?.[0];
      if (!baseOrder) return null as any;
      
      // ✅ Fix: Use baseOrder.orderedOn directly instead of order.orderDetails
      const { date: orderDate, time: orderTime } = splitDateTime(baseOrder.orderedOn?.$date ?? "");
      const customerName = [order.customerDetails?.firstName, order.customerDetails?.middleName, order.customerDetails?.lastName]
        .filter(Boolean)
        .join(" ");

      // ✅ Get status color
      const statusColor = statusColors[baseOrder?.status] || "bg-gray-100 text-gray-700";

      const row: any = {
        id: baseOrder.orderId,
        "S.No": index + 1 + page * rowsPerPage,
        Name: customerName || "N/A",
        "Phone number": String(order.customerDetails?.phones?.[0]?.phone ?? "N/A"),
        "Date / Time": (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{formatDate(orderDate)} /</span>
            <span>{formatTime(orderTime)}</span>
          </div>
        ),
        "Amount": baseOrder.finalAmount?.toFixed(2) || "0.00",
        "createdBy": baseOrder.createdBy || "N/A",
        "Order status": (
          <span
            className={`px-2 py-1 rounded font-semibold text-xs ${statusColor}`}
            style={{ 
              display: "inline-block",
              minWidth: "70px",
              textAlign: "center"
            }}
          >
            {baseOrder?.status || "N/A"}
          </span>
        ),
        Actions: (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="View order">
              <IconButton onClick={() => handleViewOrder(String(baseOrder.orderId))}>
                <ViewListIcon sx={{ color: "#000" }} />
              </IconButton>
            </Tooltip>

            {baseOrder.status === "Cancelled" && (
              <Tooltip title="Delete order">
                <IconButton onClick={() => handleOpenDialog(String(baseOrder.orderId))}>
                  <DeleteIcon sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      };

      // ✅ Conditional fields
      if (orderIdEnabled) {
        row["Order ID"] = String(baseOrder.orderId);
      }

      if (billNoEnabled) {
        row["BillNo"] = baseOrder.billNumber ? formatNumber(baseOrder.billNumber) : "N/A";
      }

      return row;
    })
    .filter(Boolean);

  return (
    <div className={`flex flex-col h-full w-full overflow-hidden p-1.5`}>
      <div className={`w-full h-full flex flex-col items-center shadow-inner rounded-lg overflow-auto ${sidebarThemeClass}`}>
        <OrdersToolbar
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          inputRef={inputRef}
          handleImport={handleImport}
          handleExport={(type) =>
            handleExport(
              type,
              orders.map((order) => ({
                ...order,
                Orderid: order.orders?.[0]?.orderId,
              })),
              selectedOption,
              searchTerm,
              page,
              rowsPerPage,
              getDateRangeForExport,
              selectedDate,
              selectedDate2,
            )
          }
          orders={orders}
          selectedDate={selectedDate}
          selectedDate2={selectedDate2}
          selectedOption={selectedOption}
          handleFilterChange={handleFilterChange}
          statusFilter={statusFilter} // ✅ Pass status filter
          onStatusFilterChange={handleStatusFilterChange} 
        />

        {selectedOption === "CustomizeDate" && (
          <div className="flex items-center gap-3 flex-wrap px-4 py-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">From</label>
              <input 
                type="date" 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)} 
                className="border rounded-md px-2 py-1" 
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">To</label>
              <input 
                type="date" 
                value={selectedDate2} 
                onChange={(e) => setSelectedDate2(e.target.value)} 
                className="border rounded-md px-2 py-1" 
              />
            </div>

            <Button label="Apply" onClick={applyCustomDateFilter} />
          </div>
        )}

        <div className="w-full overflow-y-auto">
          <CommonTable<OrderTableRow>
            data={transformedOrders}
            headCells={orderHeadCells}
            title=""
            showToolbar={false}
            selectable={false}
            denseToggle={false}
            pageSize={rowsPerPage}
            pagenation={false}
            showFilterIcon={false}
            className="shadow-inner rounded-lg bg-white p-2"
          />

          <div className="flex justify-end mr-8 mt-2">
            <TablePagination
              component="div"
              count={paginationData.totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            />
          </div>
        </div>

        <DeleteDialog open={openDialog} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} />
      </div>

      {(loading || loadings) && <LoadingScreen />}
    </div>
  );
};

export default OrderSearch;